import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

interface ImageUploaderProps {
  onImageUpload: (file: File) => void;
}

const MAX_SIZE = 42 * 1024 * 1024; // 42MB in bytes

const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageUpload }) => {
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.file.size > MAX_SIZE) {
        onImageUpload(rejection.file);
      }
      return;
    }

    if (!acceptedFiles || acceptedFiles.length === 0) {
      console.log('No files selected');
      return;
    }

    const file = acceptedFiles[0];

    // Validate image type
    if (!file.type.startsWith('image/')) {
      onImageUpload(file); // Will be handled by App.tsx error display
      return;
    }

    // Check aspect ratio
    const img = new Image();
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      
      if (Math.abs(img.width / img.height - 2) < 0.01) {
        onImageUpload(file);
      } else {
        onImageUpload(file); // Will be handled by App.tsx error display
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      onImageUpload(file); // Will be handled by App.tsx error display
    };

    try {
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (err) {
      console.error('Error creating object URL:', err);
      onImageUpload(file); // Will be handled by App.tsx error display
    }
  }, [onImageUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    multiple: false,
    maxSize: MAX_SIZE,
    minSize: 0
  });

  return (
    <div>
      <div {...getRootProps()} style={dropzoneStyles}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the image here ...</p>
        ) : (
          <p>DROP YOUR 360° IMAGE HERE, OR CLICK TO SELECT ONE.</p>
        )}
      </div>
    </div>
  );
};

const dropzoneStyles: React.CSSProperties = {
  border: '2px dashed #cccccc',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  width: '100%',
  height: '0',
  paddingBottom: '50%',
  position: 'relative',
  overflow: 'hidden'
};

export default ImageUploader;